import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { gardenAPI, serviceAPI } from '../lib/api';
import { Leaf, ArrowRight, CheckCircle, Loader2, TrendingUp, Shield, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

export default function DomainGardenPage() {
  const { t } = useLanguage();
  const { user, token } = useAuth();
  const [form, setForm] = useState({ 
    domain_name: '', 
    registrar: '',
    expiry_date: '',
    renewal_cost: '',
    reason: '',
    contact_name: '',
    contact_email: '' 
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (token) {
        // Use new garden API for logged-in users
        await gardenAPI.submit({
          ...form,
          renewal_cost: parseFloat(form.renewal_cost) || 0
        });
      } else {
        // Fallback to service request for guests
        await serviceAPI.request({ 
          ...form, 
          service_type: 'domain_garden',
          name: form.contact_name,
          email: form.contact_email
        });
      }
      setSubmitted(true);
      toast.success('Request submitted!');
    } catch (err) { 
      toast.error(err.response?.data?.detail || 'Failed to submit'); 
    }
    setLoading(false);
  };

  const benefits = [
    { icon: Shield, title: 'Domain Protection', desc: 'We keep your domain safe and renewed while you focus on other priorities' },
    { icon: TrendingUp, title: 'Value Building', desc: 'We actively work to increase your domain\'s authority, traffic, and market value' },
    { icon: Clock, title: 'Flexible Reclaim', desc: 'Reclaim your domain anytime within 12 months for just 10% of renewal cost' },
  ];

  return (
    <div className="px-6 md:px-12 max-w-7xl mx-auto py-12 md:py-20" data-testid="garden-page">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-16">
          <Leaf className="h-10 w-10 text-[#00FF94] mx-auto mb-4" />
          <h1 className="text-4xl md:text-5xl font-bold text-white font-['Outfit'] tracking-tight">{t('garden.title')}</h1>
          <p className="text-zinc-500 mt-3 max-w-xl mx-auto">{t('garden.subtitle')}</p>
        </div>

        {/* Benefits */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
          {benefits.map((benefit, i) => (
            <div key={i} className="p-6 bg-[#0A0A0A] border border-white/5 rounded-sm">
              <benefit.icon className="h-8 w-8 text-[#00FF94] mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2 font-['Outfit']">{benefit.title}</h3>
              <p className="text-sm text-zinc-500">{benefit.desc}</p>
            </div>
          ))}
        </div>

        {/* How It Works */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-16">
          {[t('garden.step1'), t('garden.step2'), t('garden.step3'), t('garden.step4')].map((step, i) => (
            <div key={i} className="p-6 bg-[#0A0A0A] border border-white/5 rounded-sm flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-[#00FF94]/10 flex items-center justify-center shrink-0">
                <span className="text-xs font-bold text-[#00FF94]">{i + 1}</span>
              </div>
              <p className="text-sm text-zinc-400">{step}</p>
            </div>
          ))}
        </div>

        {submitted ? (
          <div className="bg-[#0A0A0A] border border-white/5 rounded-sm p-12 text-center">
            <CheckCircle className="h-12 w-12 text-[#00FF94] mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Request Submitted!</h3>
            <p className="text-zinc-500">We'll review your domain and contact you within 24 hours.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-[#0A0A0A] border border-white/5 rounded-sm p-8 space-y-5" data-testid="garden-form">
            <h3 className="text-lg font-semibold text-white font-['Outfit']">Submit Your Domain to the Garden</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Your Name</Label>
                <Input 
                  value={form.contact_name} 
                  onChange={(e) => setForm(p => ({ ...p, contact_name: e.target.value }))} 
                  required
                  className="mt-1.5 h-11 bg-[#050505] border-zinc-800 focus:border-[#00FF94] text-white rounded-sm" 
                  data-testid="garden-name" 
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Email</Label>
                <Input 
                  type="email" 
                  value={form.contact_email} 
                  onChange={(e) => setForm(p => ({ ...p, contact_email: e.target.value }))} 
                  required
                  className="mt-1.5 h-11 bg-[#050505] border-zinc-800 focus:border-[#00FF94] text-white rounded-sm" 
                  data-testid="garden-email" 
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Domain Name</Label>
                <Input 
                  value={form.domain_name} 
                  onChange={(e) => setForm(p => ({ ...p, domain_name: e.target.value }))} 
                  required
                  className="mt-1.5 h-11 bg-[#050505] border-zinc-800 focus:border-[#00FF94] text-white rounded-sm" 
                  placeholder="yourdomain.com" 
                  data-testid="garden-domain" 
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Current Registrar</Label>
                <Input 
                  value={form.registrar} 
                  onChange={(e) => setForm(p => ({ ...p, registrar: e.target.value }))}
                  className="mt-1.5 h-11 bg-[#050505] border-zinc-800 focus:border-[#00FF94] text-white rounded-sm" 
                  placeholder="e.g., GoDaddy, Namecheap"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Domain Expiry Date</Label>
                <Input 
                  type="date"
                  value={form.expiry_date} 
                  onChange={(e) => setForm(p => ({ ...p, expiry_date: e.target.value }))}
                  className="mt-1.5 h-11 bg-[#050505] border-zinc-800 focus:border-[#00FF94] text-white rounded-sm"
                />
              </div>
              <div>
                <Label className="text-zinc-400 text-xs uppercase tracking-wider">Annual Renewal Cost ($)</Label>
                <Input 
                  type="number"
                  value={form.renewal_cost} 
                  onChange={(e) => setForm(p => ({ ...p, renewal_cost: e.target.value }))}
                  className="mt-1.5 h-11 bg-[#050505] border-zinc-800 focus:border-[#00FF94] text-white rounded-sm"
                  placeholder="e.g., 15"
                />
              </div>
            </div>

            <div>
              <Label className="text-zinc-400 text-xs uppercase tracking-wider">Why are you submitting this domain?</Label>
              <Textarea 
                value={form.reason} 
                onChange={(e) => setForm(p => ({ ...p, reason: e.target.value }))} 
                rows={3}
                className="mt-1.5 bg-[#050505] border-zinc-800 focus:border-[#00FF94] text-white rounded-sm resize-none" 
                placeholder="e.g., Cannot afford renewal, no longer using it, etc."
                data-testid="garden-message" 
              />
            </div>

            <Button 
              type="submit" 
              disabled={loading}
              className="h-11 px-8 bg-[#00FF94] text-black hover:bg-white font-bold uppercase tracking-wider rounded-sm shadow-[0_0_20px_rgba(0,255,148,0.3)]" 
              data-testid="garden-submit"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Submit to Garden <ArrowRight className="ml-2 h-4 w-4" /></>}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
